import { type ReactElement } from 'react';
import { DashboardMemberView } from '@utmb/types/index';
import styles from './loyalty-preview.module.scss';
import { LoyaltyLevelName } from '@utmb/types/view/LoyaltyLevelEnum';
import { Font } from '@utmb/design-system/text/font';
import { getColorStyle, getLoyaltyColor } from '@utmb/utils/colors';
import { useTranslation } from 'next-i18next';
import MyUtmbPoints from '@utmb/design-system/my-utmb-points/MyUtmbPoints';
import { Icon } from '@utmb/design-system/icons/Icon';
import { IconEnum } from '@utmb/types/enum/IconEnum';
import { ColorTypesEnum } from '@utmb/types/colors';
import Link from 'next/link';

export interface LoyaltyPreviewProps {
    dashboardMember: DashboardMemberView;
    href?: string;
}

export function LoyaltyPreview({ dashboardMember, href }: LoyaltyPreviewProps): ReactElement | null {
    const { t } = useTranslation('myutmb');
    if (!dashboardMember.loyalty) return null;
    const max = dashboardMember.loyalty.levels[dashboardMember.loyalty.levels.length - 1].minPoints;

    function getSpecialCursor(value: number): string {
        let className = '';
        dashboardMember.loyalty?.levels.forEach((level) => {
            if (level.minPoints === value) {
                className = styles.whiteCursor;
            }
        });
        return className;
    }

    const content = (
        <div className={styles.loyaltyPreview}>
            <div className={styles.wrapFourPlus}>
                <Icon variant={IconEnum.FOURPLUS} color={getLoyaltyColor(dashboardMember.loyalty.currentLevel)} width={150} height={150} />
            </div>
            <div className={styles.header}>
                <Font.FuturaMedium as="span">{t('myUtmb.rewards.subtitle1')}</Font.FuturaMedium>
                &nbsp;
                <Font.FuturaBold as="span">{t('myUtmb.rewards.subtitle2')}</Font.FuturaBold>
            </div>
            <div className={styles.content}>
                <MyUtmbPoints quantity={dashboardMember.loyalty.points} color={getLoyaltyColor(dashboardMember.loyalty.currentLevel)} size={100} />
                <div className={styles.wrapTitle}>
                    <Font.FuturaBold desktop="40" mobile="40" className={styles.title} as="span">
                        {LoyaltyLevelName[dashboardMember.loyalty.currentLevel]}
                    </Font.FuturaBold>
                    <div className={styles.wrapLinePlus}>
                        <Icon variant={IconEnum.LINEPLUS} width={250} color={getColorStyle(ColorTypesEnum.WHITE)} />
                    </div>
                </div>
            </div>
            <div className={styles.bottom}>
                <div className={styles.wrapBar}>
                    {dashboardMember.loyalty.points ? (
                        <div className={styles.bar} style={{ width: `${(dashboardMember.loyalty.points * 100) / max}%` }}></div>
                    ) : null}
                    <div className={styles.wrapCursors}>
                        {Array.from({ length: (max * 2) / 10 + 1 }).map((_, i) => (
                            <div key={i} className={[styles.cursor, getSpecialCursor(i * 5), i % 2 !== 0 ? styles.halfCursor : ''].join(' ')}></div>
                        ))}
                    </div>
                    <div className={styles.wrapLevels}>
                        {dashboardMember.loyalty.levels
                            .filter((target) => target.level !== 0)
                            .map((level) => (
                                <div
                                    key={level.level}
                                    style={{ left: `${(level.minPoints * 100) / max}%` }}
                                    className={`${styles.level} ${styles['level' + level.level]}`}
                                >
                                    <Font.FuturaHeavy desktop="16" mobile="14">
                                        {level.minPoints}
                                    </Font.FuturaHeavy>
                                    <Font.FuturaHeavy className={styles.levelNameText} desktop="16" mobile="14" color={getLoyaltyColor(level.level)}>
                                        {LoyaltyLevelName[level.level]}
                                    </Font.FuturaHeavy>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );

    return href ? (
        <Link href={href}>
            <div className={styles.linkContent}>{content}</div>
        </Link>
    ) : (
        <>{content}</>
    );
}
